import { UserCog, Shield, LayoutDashboard, type LucideIcon } from "lucide-react";
import { adminSidebarGroups, type AdminSidebarItem } from "@/config/adminSidebarConfig";

export interface SearchEntry {
  key: string;
  title: string;
  subtitle?: string;
  category: string;
  route: string;
  icon: LucideIcon;
  keywords: string[];
  permission?: string;
  moduleKey?: string;
  priority: number;
}

/** Extra synonyms so legacy aliases keep working. Keyed by route. */
const KEYWORD_OVERRIDES: Record<string, string[]> = {
  "/admin/dashboard": ["home", "stats", "overview", "kpi", "metrics"],
  "/admin/users": ["accounts", "members", "clients"],
  "/admin/kyc": ["verification", "identity", "compliance", "pending"],
  "/admin/name-changes": ["rename", "legal name", "proof"],
  "/admin/aml-screenings": ["aml", "sanctions", "pep", "screening"],
  "/admin/sar-filing": ["sar", "suspicious", "report", "fincen"],
  "/admin/exchanges": ["swap", "convert"],
  "/admin/transactions": ["ledger", "history", "payments"],
  "/admin/crypto-deposits": ["bitcoin", "ethereum", "blockchain"],
  "/admin/crypto-withdrawals": ["approve", "pending", "blockchain"],
  "/admin/payouts": ["payout", "coinsbuy", "retry", "settlement", "tx hash", "fee"],
  "/admin/crypto-callback-logs": ["webhook", "coinsbuy", "provider"],
  "/admin/deposit-reconciliation": ["reconcile", "mismatch", "ledger", "credit", "replay", "anomaly"],
  "/admin/crypto-addresses": ["registry", "address", "deposit"],
  "/admin/virtual-cards": ["visa", "mastercard", "card"],
  "/admin/p2p": ["peer", "otc"],
  "/admin/p2p-disputes": ["conflict", "arbitration"],
  "/admin/spot-markets": ["pair", "trading"],
  "/admin/ai-market-maker": ["bot", "liquidity", "scalper", "swing"],
  "/admin/futures-contracts": ["perpetual", "leverage"],
  "/admin/futures-positions": ["liquidation"],
  "/admin/data-migration": ["import", "legacy", "transfer"],
  "/admin/token-config": ["coin", "currency", "branding"],
  "/admin/my-security": ["password", "2fa", "mfa", "totp"],
  "/admin/mobile-api": ["flutter", "mobile", "api", "console"],
  "/admin/security-center": ["security", "pentest", "audit", "scan"],
  "/admin/risk-center": ["risk", "freeze", "staff location", "esrcc", "security events"],
  "/admin/modules": ["module", "enable", "disable", "visibility", "feature flag"],
};

const slugWords = (route: string) =>
  route
    .replace(/^\/admin\/?/, "")
    .split(/[/-]/)
    .filter(Boolean);

function flattenItem(
  item: AdminSidebarItem,
  groupLabel: string,
  parentLabel: string | undefined,
  basePriority: number,
  out: SearchEntry[],
) {
  if (item.children && item.children.length > 0) {
    item.children.forEach((child, i) =>
      flattenItem(child, groupLabel, item.label, basePriority + i, out),
    );
    return;
  }
  out.push({
    key: item.to,
    title: item.label,
    subtitle: parentLabel ? `${groupLabel} › ${parentLabel}` : groupLabel,
    category: groupLabel,
    route: item.to,
    icon: item.icon,
    keywords: [
      ...slugWords(item.to),
      ...(parentLabel ? parentLabel.toLowerCase().split(/\s+/) : []),
      ...groupLabel.toLowerCase().split(/\s+/),
      ...(KEYWORD_OVERRIDES[item.to] ?? []),
    ],
    permission: item.permission,
    moduleKey: item.moduleKey,
    priority: basePriority,
  });
}

function buildIndex(): SearchEntry[] {
  const out: SearchEntry[] = [
    {
      key: "/admin/dashboard",
      title: "Dashboard",
      subtitle: "Platform overview & KPIs",
      category: "Overview",
      route: "/admin/dashboard",
      icon: LayoutDashboard,
      keywords: KEYWORD_OVERRIDES["/admin/dashboard"],
      permission: "dashboard.view",
      priority: 0,
    },
  ];

  adminSidebarGroups.forEach((group, gi) => {
    group.items.forEach((item, ii) =>
      flattenItem(item, group.label, undefined, (gi + 1) * 1000 + ii * 10, out),
    );
  });

  // Account entries (not part of the sidebar config)
  out.push(
    {
      key: "/admin/my-settings",
      title: "My Settings",
      subtitle: "Your admin profile",
      category: "Account",
      route: "/admin/my-settings",
      icon: UserCog,
      keywords: ["my", "settings", "profile", "account"],
      priority: 900000,
    },
    {
      key: "/admin/my-security",
      title: "My Security",
      subtitle: "Your security settings",
      category: "Account",
      route: "/admin/my-security",
      icon: Shield,
      keywords: KEYWORD_OVERRIDES["/admin/my-security"],
      priority: 900001,
    },
  );

  // De-duplicate by route, keeping the first (lowest priority) occurrence
  const seen = new Set<string>();
  return out.filter((e) => (seen.has(e.route) ? false : (seen.add(e.route), true)));
}

export const ADMIN_SEARCH_INDEX: SearchEntry[] = buildIndex();

/** Score an entry against a lowercased query. Returns -1 when it does not match. */
export function scoreEntry(entry: SearchEntry, query: string): number {
  const title = entry.title.toLowerCase();
  const haystack = [
    title,
    entry.subtitle?.toLowerCase() ?? "",
    entry.category.toLowerCase(),
    entry.route.toLowerCase(),
    entry.keywords.join(" ").toLowerCase(),
  ].join(" ");

  const words = query.split(/\s+/).filter(Boolean);
  if (!words.every((w) => haystack.includes(w))) return -1;

  if (title === query) return 0;
  if (title.startsWith(query)) return 1;
  if (title.includes(query)) return 2;
  if (entry.category.toLowerCase().includes(query)) return 3;
  return 4;
}
